import * as Sentry from '@sentry/react';
import FeatherIcon from 'feather-icons-react';
import { Button } from 'react-bootstrap';

import { Customer } from '../types';

interface PrintOrderButtonProps {
  customer: Customer | Omit<Customer, 'createdBy' | 'updatedBy'>;
  orderNumber: string;
  orderSummaryRef: React.RefObject<HTMLDivElement>;
  paperSize?: 'a4' | 'pos';
  label?: string;
  preview?: boolean;
}

const PrintDetailsButton = ({
  orderNumber,
  customer,
  orderSummaryRef,
  paperSize = 'a4',
  label = 'Print',
  preview = false
}: PrintOrderButtonProps) => {
  const handlePrint = () => {
    if (orderSummaryRef.current) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        const content = orderSummaryRef.current.innerHTML;

        const styleSheets = Array.from(document.styleSheets);
        let allStyles = '';

        styleSheets.forEach(sheet => {
          try {
            const cssRules = Array.from(sheet.cssRules);
            cssRules.forEach(rule => {
              allStyles += rule.cssText;
            });
          } catch (e) {
            console.warn('Cannot read stylesheet', e);
            Sentry.captureException(e);
          }
        });

        const isPOS = paperSize === 'pos';

        // Shared layout overrides for 80mm — collapses Bootstrap grid, strips padding, fixes table overflow
        const posLayoutOverrides = `
          .card { border: none !important; box-shadow: none !important; }
          .card-body { padding: 6px !important; }
          .row { margin-left: 0 !important; margin-right: 0 !important; }
          [class*="col-"] { flex: 0 0 100% !important; max-width: 100% !important; padding-left: 0 !important; padding-right: 0 !important; }
          [class*="px-"] { padding-left: 4px !important; padding-right: 4px !important; }
          [class*="mb-"] { margin-bottom: 4px !important; }
          [class*="mt-"] { margin-top: 4px !important; }
          .text-md-end, .text-end, .customer-info { text-align: left !important; }
          .table-responsive { overflow: visible !important; }
          table { width: 100% !important; table-layout: fixed !important; border-collapse: collapse !important; font-size: 10px !important; }
          th, td { padding: 2px 3px !important; word-break: break-word !important; overflow: hidden !important; }
          table th:nth-child(1), table td:nth-child(1) { width: 42%; }
          table th:nth-child(2), table td:nth-child(2) { width: 13%; text-align: center !important; }
          table th:nth-child(3), table td:nth-child(3) { width: 22%; }
          table th:nth-child(4), table td:nth-child(4) { width: 23%; }
          h2 { font-size: 14px !important; margin: 4px 0 !important; }
          h4 { font-size: 12px !important; margin: 3px 0 !important; }
          h5, h6 { font-size: 10px !important; margin: 2px 0 !important; }
          .h3 { font-size: 12px !important; }
          p { margin-bottom: 3px !important; }
        `;

        const printStyles = isPOS
          ? `
            @media print {
              @page { size: 80mm auto; margin: 0mm; }
              body {
                width: 80mm;
                margin: 0;
                padding: 2mm;
                font-size: 11px;
                print-color-adjust: exact;
                -webkit-print-color-adjust: exact;
              }
              ${posLayoutOverrides}
            }
            ${
              preview
                ? `
              body { background: #d0d0d0; padding: 16px; }
              #pos-paper {
                background: white;
                width: 100%;
                max-width: 302px;
                margin: 0 auto;
                padding: 6px;
                box-shadow: 0 2px 12px rgba(0,0,0,0.3);
                font-size: 11px;
              }
              ${posLayoutOverrides}
            `
                : ''
            }
          `
          : `
            @media print {
              body { print-color-adjust: exact; -webkit-print-color-adjust: exact; margin: 20mm 10mm 10mm 10mm; }
              @page { size: auto; margin: 0mm; }
              .customer-info { text-align: right !important; }
              h4 { page-break-before: auto; page-break-after: avoid; padding-top: 8mm; }
              .order-items-section { page-break-inside: avoid; }
              table { page-break-inside: avoid; }
              tr { page-break-inside: avoid; page-break-after: auto; }
            }
          `;

        const viewportMeta = isPOS ? `<meta name="viewport" content="width=302">` : '';

        const bodyContent = isPOS
          ? `<div id="pos-paper">${content}</div>`
          : `
            <div class="container-fluid">
              <div class="row justify-content-center">
                <div class="col-12 col-lg-10 col-xl-8">
                  ${content}
                </div>
              </div>
            </div>
          `;

        printWindow.document.write(`
              <!DOCTYPE html>
              <html>
                <head>
                  <title>${preview ? '[Preview] ' : ''}${customer?.name} - ${orderNumber}</title>
                  ${viewportMeta}
                  <style>
                    ${allStyles}
                    ${printStyles}
                  </style>
                </head>
                <body>
                  ${bodyContent}
                </body>
              </html>
            `);
        printWindow.document.close();
        printWindow.focus();
        if (!preview) {
          printWindow.print();
        }
      }
    }
  };

  return (
    <Button variant={preview ? 'outline-info' : 'outline-secondary'} onClick={handlePrint}>
      <FeatherIcon className="me-2" icon={preview ? 'eye' : 'printer'} size="1em" />
      {label}
    </Button>
  );
};

export default PrintDetailsButton;
